/**
 * Admin Impersonation Panel Component
 *
 * Admin UI to search and impersonate organizations for customer support.
 *
 * DESIGN:
 * - Search input for organization name
 * - List of organizations with details (name, type, vessel count)
 * - "Impersonate" button per org
 * - Active session indicator
 * - Warning about audit trail
 *
 * FEATURES:
 * - Card with rounded-2xl, shadow-md, slate-800 header
 * - Search filtering
 * - Reason input modal
 * - Only visible to super_admin role
 *
 * INTEGRATION:
 * - Fetch from organizations table via Supabase
 * - Uses useImpersonation hook
 */

import React, { useState, useEffect } from 'react';
import {
  Search,
  Eye,
  Building2,
  Ship,
  Users,
  AlertTriangle,
  Loader2,
  Clock,
  Shield
} from 'lucide-react';
// TODO: Migrate to Azure impersonation APIs (GET /api/admin/organizations)
// import { getSupabaseClient } from '../../../lib/supabase';
import { useImpersonation } from '../../../platform/auth/impersonationProvider';
import { devLog } from '../../../utils/devLog';

interface Organization {
  id: string;
  name: string;
  slug: string;
  tier: string;
  created_at: string;
  vessel_count?: number;
  member_count?: number;
}

export function AdminImpersonationPanel() {
  const { session, startImpersonation, isImpersonating } = useImpersonation();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [filteredOrgs, setFilteredOrgs] = useState<Organization[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [reason, setReason] = useState('');
  const [isStarting, setIsStarting] = useState(false);

  // Load organizations
  useEffect(() => {
    loadOrganizations();
  }, []);

  // Filter organizations on search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOrgs(organizations);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = organizations.filter(org =>
      org.name.toLowerCase().includes(term) ||
      org.slug.toLowerCase().includes(term)
    );
    setFilteredOrgs(filtered);
  }, [searchTerm, organizations]);

  const loadOrganizations = async () => {
    // TODO: Migrate to Azure API - GET /api/admin/organizations
    // For now, show graceful degradation message
    setIsLoading(false);
    setError('Impersonation organization list temporarily unavailable during Azure migration. Impersonation functionality via direct URL will be restored in Phase 7-8.');
    setOrganizations([]);
    setFilteredOrgs([]);

    /* ORIGINAL SUPABASE CODE - TO BE REPLACED WITH AZURE API
    try {
      setIsLoading(true);
      setError(null);

      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase client not available');
      }

      // Fetch organizations with counts
      const { data, error: fetchError } = await supabase
        .from('organizations')
        .select(`
          id,
          name,
          slug,
          tier,
          created_at,
          vessels (count),
          organization_members (count)
        `)
        .order('name', { ascending: true });

      if (fetchError) {
        throw fetchError;
      }

      // Transform data to include counts
      const orgsWithCounts = (data || []).map(org => ({
        ...org,
        vessel_count: org.vessels?.[0]?.count || 0,
        member_count: org.organization_members?.[0]?.count || 0
      }));

      setOrganizations(orgsWithCounts);
      setFilteredOrgs(orgsWithCounts);

      devLog('[IMPERSONATE] Loaded organizations:', orgsWithCounts.length);

    } catch (err: any) {
      console.error('[IMPERSONATE] Failed to load organizations:', err);
      setError(err.message || 'Failed to load organizations');
    } finally {
      setIsLoading(false);
    }
    */
  };

  const handleImpersonateClick = (org: Organization) => {
    setSelectedOrg(org);
    setReason('');
    setShowReasonModal(true);
  };

  const handleStartImpersonation = async () => {
    if (!selectedOrg || !reason.trim()) {
      return;
    }

    setIsStarting(true);
    try {
      await startImpersonation(selectedOrg.id, selectedOrg.name, reason.trim());
      setShowReasonModal(false);
      setSelectedOrg(null);
      setReason('');
    } catch (err: any) {
      console.error('[IMPERSONATE] Failed to start:', err);
      alert(err.message || 'Failed to start impersonation session');
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-yellow-900 mb-1">
              Admin Impersonation - Use With Care
            </h3>
            <p className="text-yellow-800 text-sm">
              All impersonation sessions are logged and visible to customers in their Access Log.
              Only use this feature for legitimate customer support purposes.
              Sessions expire after 24 hours.
            </p>
          </div>
        </div>
      </div>

      {/* Active Session Indicator */}
      {isImpersonating && session && (
        <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-red-600" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-900">
                Currently Impersonating: {session.organizationName}
              </h3>
              <p className="text-red-700 text-sm mt-1">
                You are viewing and editing data as this organization. End your session before impersonating another.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
        <input
          type="text"
          placeholder="Search organizations by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Organizations List */}
      {!isLoading && !error && (
        <div className="space-y-3">
          {filteredOrgs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              {searchTerm ? 'No organizations match your search' : 'No organizations found'}
            </div>
          ) : (
            filteredOrgs.map(org => (
              <div
                key={org.id}
                className="bg-white border border-slate-200 rounded-xl p-4 hover:border-cyan-300 transition-colors"
              >
                <div className="flex items-center justify-between gap-4">
                  {/* Organization Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                      <h3 className="font-semibold text-slate-900 truncate">
                        {org.name}
                      </h3>
                      <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                        {org.tier}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <Ship className="h-4 w-4" />
                        <span>{org.vessel_count || 0} vessels</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{org.member_count || 0} members</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>Created {new Date(org.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Impersonate Button */}
                  <button
                    onClick={() => handleImpersonateClick(org)}
                    disabled={isImpersonating && session?.targetOrganizationId !== org.id}
                    className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <Eye className="h-4 w-4" />
                    Impersonate
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Reason Modal */}
      {showReasonModal && selectedOrg && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            {/* Modal Header */}
            <div className="bg-gray-900 text-white px-6 py-4 rounded-t-2xl">
              <h2 className="text-lg font-semibold">Start Impersonation Session</h2>
              <p className="text-slate-300 text-sm mt-1">
                {selectedOrg.name}
              </p>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-yellow-800 text-sm">
                    This session will be logged and visible to the customer. Session expires in 24 hours.
                  </p>
                </div>
              </div>

              {/* Reason Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Reason for Impersonation (Required) *
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Customer support ticket #12345 - troubleshooting vessel settings issue"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                  rows={3}
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-1">
                  This reason will be shown to the customer in their access log
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowReasonModal(false);
                    setSelectedOrg(null);
                    setReason('');
                  }}
                  disabled={isStarting}
                  className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStartImpersonation}
                  disabled={!reason.trim() || isStarting}
                  className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isStarting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    'Start Session'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
